import i18n from "i18next";
import moment, { Moment } from "moment";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/redux/hooks";
import { ClientSettingsState } from "src/redux/slices/clientSettingsSlice";

interface LocalizationHook {
  formatDate: (dateString: string | Date) => string;
  formatTime: (dateString: string | Date) => string;
  formatDateTime: (dateTimeString: string | Date) => string;
  formatCurrency: (amount?: number | null) => string;
  currentLocale: string;
  formatPercentage: (amount: number) => string;
  formatMileage: (val: any) => string;
  formatMileagename: () => string;
  formatAddCode: () => string;
  formatProvince: () => string;
}

const useLocalization = (): LocalizationHook => {
  const {
    dateTimeFormat,
    dateOnlyFormat,
    language: currentLocale,
    mileageUnit,
  }: ClientSettingsState = useAppSelector((state) => state.clientSettings);
  const { t } = useTranslation();
  // const [currentLocale, setCurrentLocale] = useState<string>("en-US");

  useEffect(() => {
    moment.locale(currentLocale || "en-US");
    i18n.changeLanguage(currentLocale || "en-US");
  }, [currentLocale]);

  const formatDate = (dateString: string | Date): string => {
    if (typeof dateString === "string") {
      const date = moment(dateString).format(dateOnlyFormat);

      return t("dateFormat", { date });
    } else {
      return moment(dateString).format(dateOnlyFormat);
    }
  };

  const formatDateTime = (dateTimeString: string | Date | Moment): string => {
    const date = moment(dateTimeString).format(dateTimeFormat);
    return t("datetimeFormat", { date });
  };

  const formatTime = (dateTimeString: string | Date | Moment): string => {
    const timeFormat = "h:mm A"; // Changed format string
    const date = moment(dateTimeString).format("hh:mm A");
    return date;
  };

  const formatMileage = (val: any) => {
    let unit = "";
    if (mileageUnit === "Kilometers") unit = "km";
    else if (mileageUnit === "Miles") unit = "mi";
    return `${val}${unit}`;
  };

  const formatMileagename = () => {
    let unit = "";
    if (mileageUnit === "Kilometers") unit = "Kilometer";
    else if (mileageUnit === "Miles") unit = "Milage";
    return `${unit}`;
  };

  const formatAddCode = (): string => {
    return currentLocale === "en-US" ? "ZIP code" : "Postal code";
  };

  const formatProvince = (): string => {
    return currentLocale === "en-US" ? "State" : "Province";
  };

  const formatCurrency = (amount?: number | null): string => {
    const formattedAmount = new Intl.NumberFormat(currentLocale || "en-US", {
      style: "currency",
      currency: currentLocale === "en-US" ? "USD" : "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount ?? 0);

    return formattedAmount;
  };

  const formatPercentage = (amount: number): string => {
    const formattedPercentage = new Intl.NumberFormat(
      currentLocale || "en-US",
      {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(amount / 100);

    return formattedPercentage;
  };

  return {
    currentLocale,
    formatDate,
    formatTime,
    formatDateTime,
    formatCurrency,
    formatPercentage,
    formatMileage,
    formatMileagename,
    formatAddCode,
    formatProvince,
  };
};

export default useLocalization;
